import React, { useState, useEffect } from "react";
import { useParams } from 'react-router-dom';
import buxferService from '../services/buxfer.service';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Chip,
    Avatar,
    Grid,
    Divider,
    InputAdornment,
    Button,
    Paper,
    TextField,
    FormControl,
    InputLabel,
    Stack,
    Select,
    MenuItem,
    IconButton
} from "@mui/material";
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BusinessIcon from '@mui/icons-material/Business';
import CreditUtilizationPanel from '../components/CreditUtilizationPanel'; // Make sure tama path
import BalanceOverviewPanel from '../components/BalanceOverviewPanel';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(Number(amount || 0));
};

export default function SingleCustomerPage() {
        const { customer_code } = useParams();
  
    // Data States
    const [customerProfile, setCustomerProfile] = useState(null);
    const [bankListData, setBankListData] = useState([]); 
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedBank, setSelectedBank] = useState('');
    const [selectedType, setSelectedType] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [accountStats, setAccountStats] = useState([]);


    // Fetch Data Effect
    useEffect(() => {
        const fetchData = async () => {
        if (!customer_code) return;

                console.log("Fetching profile for customer_code (from param):", customer_code);

                try {
                    const profileData = await buxferService.getCustomerProfileByCode(customer_code);
            console.log(profileData);
            if (profileData && profileData.success && profileData.profile) {
            setCustomerProfile({
                name: profileData.profile.full_name,
                email: profileData.profile.cc_email,
                creditScore: 750, // Mock
                riskLevel: "Low", // Mock
                relationshipValue: profileData.profile.total_balance || "0",
                accountsCount: profileData.profile.accounts_count || 0,
                creditLimit: "N/A",
                utilization: "N/A"
            });

            // Use banks array from backend instead of recalculating
            if (profileData.profile.banks && profileData.profile.banks.length > 0) {
                setBankListData(profileData.profile.banks.map(b => ({
                    bankName: b.name,
                    relationship: 'Standard Relationship',
                    totalBalance: b.balance,
                    creditCardsCount: b.count,
                    lastActivity: b.last_synced || ''
                })));
            } else {
                setBankListData([
                    { bankName: "No Data", relationship: "-", totalBalance: 0, creditCardsCount: 0, lastActivity: "-" }
                ]);
            }
            // build accountStats for the overview/utilization panels if accounts included
            if (Array.isArray(profileData.profile.accounts)) {
                // Only show accounts with client_id
                const filtered = profileData.profile.accounts.filter(a => a && a.client_id);
                const grouped = {};
                filtered.forEach(acc => {
                    const bankName = acc.bank || 'Unknown';
                    if (!grouped[bankName]) {
                        grouped[bankName] = {
                            bankName,
                            relationship: 'Derived',
                            totalBalance: 0,
                            creditCardsCount: 0,
                            depositCount: 0,
                            lastActivity: acc.last_synced || '',
                            accounts: []
                        };
                    }
                    const bucket = grouped[bankName];
                    const rawBal = parseFloat(acc.balance) || 0;
                    bucket.totalBalance += rawBal;
                    const prettyType = (acc.type && acc.type.toLowerCase().includes('deposit')) ? 'Deposit' : 'Credit';
                    if (prettyType === 'Deposit') bucket.depositCount++;
                    else bucket.creditCardsCount++;
                    bucket.accounts.push({
                        id: acc.id,
                        prettyName: acc.name,
                        prettyType,
                        card_number: acc.card_number || '',
                        total_spend: rawBal,
                        credit_limit: acc.total_credit_line || 0,
                        available_credit: acc.available_credit,
                        apr: acc.apr || 0
                    });
                });
                setAccountStats(Object.values(grouped));
            }
            } else {
                // Fallback Visual
                setCustomerProfile({
                    name: customer_code,
                    email: "loading...",
                    relationshipValue: "0",
                    accountsCount: 0
                });
            }
        } catch (error) {
            console.error("Failed to fetch data:", error);
        }
        };

        fetchData();
    }, [customer_code]);

    // Autopay data
    const [autopayRows, setAutopayRows] = useState([]);
    useEffect(() => {
        const loadAutopay = async () => {
            if (!customer_code) return;
                            let resp;
                            try {
                                    const profileUserIds = (customerProfile && customerProfile.user_ids) || null;
                                    if (Array.isArray(profileUserIds) && profileUserIds.length) {
                                        resp = await buxferService.getActivityStatementAutopay({ user_ids: profileUserIds, page: 1, pageSize: 50 });
                                    } else {
                                        setAutopayRows([]);
                                        return;
                                    }
                if (resp && resp.success && resp.result && Array.isArray(resp.result.rows)) {
                    setAutopayRows(resp.result.rows);
                } else {
                    setAutopayRows([]);
                }
            } catch (err) {
                console.error('Failed to load autopay rows', err);
                setAutopayRows([]);
            }
        };
        loadAutopay();
    }, [customer_code, customerProfile]);

    // Derived options for filters
    const bankOptions = Array.from(new Set(bankListData.map(b => b.bankName).filter(Boolean)));
    const accountTypeOptions = ['All Types', 'Credit Card', 'Debit', 'Loan']; // placeholder
    const statusOptions = ['All Status', 'Active', 'Closed', 'Delinquent']; // placeholder

    const handleClearFilters = () => {
        setSearchQuery('');
        setSelectedBank('');
        setSelectedType('');
        setSelectedStatus('');
    };

    const shownBanksCount = bankListData.length;
    const totalAccounts = bankListData.reduce((s, b) => s + (Number(b.creditCardsCount) || 0), 0);

  return (
    // DARK BACKGROUND
    <Box p={3} bgcolor="#121212" minHeight="100vh" color="#fff">
        {/* 1. Header */}
        <Box mb={3}>
            <Typography variant="h5" fontWeight="bold" color="#fff">
                Customer Relationship Management - Single Customer
            </Typography>
            <Typography variant="body2" sx={{ color: '#b0b3b8' }}>
                Comprehensive banking relationship overview for <span style={{color: '#90caf9'}}>{customerProfile?.name || '...'}</span>
            </Typography>
        </Box>

        {/* 2. Simplified Profile Card */}
        <Card sx={{ 
            borderRadius: 4, // Mas rounded para modern
            boxShadow: "0 8px 24px rgba(0,0,0,0.4)", // Mas malalim na shadow
            mb: 4, 
            bgcolor: '#1e1e1e', 
            color: '#fff',
            border: '1px solid #333',
            position: 'relative',
            overflow: 'hidden'
        }}>
            {/* Decorative Top Accent (Optional: pampaganda lang) */}
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, bgcolor: '#1565c0' }} />

            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                {/* GAMIT KA NG BOX FLEX INSTEAD OF GRID */}
                {/* Ito ang magic: justifyContent="space-between" + alignItems="flex-start" or "center" */}
                <Box 
                    display="flex" 
                    flexDirection={{ xs: 'column', md: 'row' }} // Column sa mobile, Row sa desktop
                    alignItems={{ xs: 'flex-start', md: 'center' }} // Center alignment vertically
                    justifyContent="space-between" 
                    gap={3}
                >
                    
                    {/* --- LEFT SIDE: PROFILE INFO --- */}
                    <Box display="flex" alignItems="center" gap={3}>
                        {/* Avatar with Ring */}
                        <Avatar sx={{ 
                            width: 80, 
                            height: 80, 
                            bgcolor: '#1565c0', 
                            fontSize: '2rem', 
                            fontWeight: 'bold',
                            border: '4px solid rgba(21, 101, 192, 0.2)' // Ring effect
                        }}>
                            {customerProfile?.name ? customerProfile.name.charAt(0) : 'U'}
                        </Avatar>

                        <Box>
                            <Typography variant="h5" fontWeight="800" sx={{ lineHeight: 1.2 }}>
                                {customerProfile?.name || 'Loading...'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#b0b3b8', mb: 1.5 }}>
                                {customerProfile?.email}
                            </Typography>
                            
                            {/* Chips Row */}
                            <Box display="flex" gap={1} flexWrap="wrap">
                                <Chip 
                                    label={`Credit Score: ${customerProfile?.creditScore || 'N/A'}`} 
                                    size="small" 
                                    sx={{ 
                                        bgcolor: '#2c2c2c', 
                                        color: '#e0e0e0', 
                                        fontWeight: 600, 
                                        border: '1px solid #444',
                                        height: 24
                                    }} 
                                />
                                <Chip 
                                    label={`Risk: ${customerProfile?.riskLevel || 'N/A'}`} 
                                    size="small" 
                                    sx={{ 
                                        bgcolor: 'rgba(102, 187, 106, 0.1)', 
                                        color: '#66bb6a', 
                                        fontWeight: 600, 
                                        border: '1px solid rgba(102, 187, 106, 0.3)',
                                        height: 24
                                    }} 
                                />
                            </Box>
                        </Box>
                    </Box>

                    {/* --- RIGHT SIDE: RELATIONSHIP VALUE --- */}
                    {/* Sa mobile: Left align, Sa desktop: Right align */}
                    <Box sx={{ textAlign: { xs: 'left', md: 'right' }, minWidth: 'fit-content' }}>
                        <Typography variant="caption" sx={{ color: '#90caf9', fontWeight: 600, letterSpacing: 1.2, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                            Total Relationship Value
                        </Typography>
                        
                        <Typography variant="h3" fontWeight="bold" sx={{ color: '#fff', lineHeight: 1 }}>
                            {formatCurrency(customerProfile?.relationshipValue)}
                        </Typography>
                        
                        <Typography variant="body2" sx={{ color: '#b0b3b8', mt: 0.5 }}>
                            Across {customerProfile?.accountsCount || 0} active accounts
                        </Typography>
                    </Box>

                </Box>
            </CardContent>
        </Card>

        {/* 3. Search & Filter (mock UI) */}
        <Paper sx={{ 
            p: 2.5, 
            mb: 4, 
            bgcolor: '#1e1e1e', 
            borderRadius: 3,
            border: '1px solid #333',
            boxShadow: '0 4px 24px rgba(0,0,0,0.25)' 
        }}>
            {/* 1. SEARCH BAR (Full Width) */}
            <Box mb={2.5}>
                <TextField
                    fullWidth
                    placeholder="Search accounts..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    variant="outlined"
                    size="medium" // Mas malaki ng onti para premium feel
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: '#666' }} />
                            </InputAdornment>
                        ),
                        sx: { 
                            bgcolor: '#121212', 
                            color: '#fff',
                            borderRadius: 2,
                            fontSize: '1rem',
                            '& fieldset': { border: '1px solid #333' },
                            '&:hover fieldset': { borderColor: '#555' },
                            '&.Mui-focused fieldset': { borderColor: '#90caf9' } // Blue highlight pag click
                        }
                    }}
                />
            </Box>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)', mb: 2.5 }} />

            {/* 2. FILTERS ROW (Gamit ang Stack para hindi mapipi) */}
            <Stack 
                direction={{ xs: 'column', md: 'row' }} 
                spacing={2} 
                alignItems="center"
            >
                {/* Filter: Bank */}
                <FormControl size="small" sx={{ minWidth: 200, width: { xs: '100%', md: 'auto' } }}>
                    <Select 
                        value={selectedBank} 
                        onChange={(e) => setSelectedBank(e.target.value)}
                        displayEmpty
                        renderValue={(selected) => {
                            if (!selected) return <span style={{ color: '#777' }}>Filter by Bank</span>;
                            return selected;
                        }}
                        sx={{
                            bgcolor: '#252525',
                            color: '#fff',
                            borderRadius: 1.5,
                            '.MuiOutlinedInput-notchedOutline': { borderColor: '#444' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#666' },
                            '& .MuiSvgIcon-root': { color: '#777' }
                        }}
                    >
                        <MenuItem value="">All Banks</MenuItem>
                        {bankOptions.map((b, i) => <MenuItem key={i} value={b}>{b}</MenuItem>)}
                    </Select>
                </FormControl>

                {/* Filter: Account Type */}
                <FormControl size="small" sx={{ minWidth: 180, width: { xs: '100%', md: 'auto' } }}>
                    <Select 
                        value={selectedType} 
                        onChange={(e) => setSelectedType(e.target.value)}
                        displayEmpty
                        renderValue={(selected) => {
                            if (!selected) return <span style={{ color: '#777' }}>Account Type</span>;
                            return selected;
                        }}
                        sx={{
                            bgcolor: '#252525',
                            color: '#fff',
                            borderRadius: 1.5,
                            '.MuiOutlinedInput-notchedOutline': { borderColor: '#444' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#666' },
                            '& .MuiSvgIcon-root': { color: '#777' }
                        }}
                    >
                        <MenuItem value="">All Types</MenuItem>
                        {accountTypeOptions.map((t, i) => <MenuItem key={i} value={t}>{t}</MenuItem>)}
                    </Select>
                </FormControl>

                {/* Filter: Status */}
                <FormControl size="small" sx={{ minWidth: 160, width: { xs: '100%', md: 'auto' } }}>
                    <Select 
                        value={selectedStatus} 
                        onChange={(e) => setSelectedStatus(e.target.value)}
                        displayEmpty
                        renderValue={(selected) => {
                            if (!selected) return <span style={{ color: '#777' }}>Status</span>;
                            return selected;
                        }}
                        sx={{
                            bgcolor: '#252525',
                            color: '#fff',
                            borderRadius: 1.5,
                            '.MuiOutlinedInput-notchedOutline': { borderColor: '#444' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#666' },
                            '& .MuiSvgIcon-root': { color: '#777' }
                        }}
                    >
                        <MenuItem value="">All Status</MenuItem>
                        {statusOptions.map((s, i) => <MenuItem key={i} value={s}>{s}</MenuItem>)}
                    </Select>
                </FormControl>

                {/* Spacer to push Reset button to the right (Optional) */}
                <Box sx={{ flexGrow: 1 }} />

                {/* Reset & Count */}
                <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="body2" sx={{ color: '#555', whiteSpace: 'nowrap' }}>
                        {totalAccounts} results
                    </Typography>
                    <Button 
                        onClick={handleClearFilters}
                        size="small"
                        variant="text"
                        sx={{ 
                            color: '#ef5350', 
                            whiteSpace: 'nowrap',
                            '&:hover': { bgcolor: 'rgba(239, 83, 80, 0.1)' } 
                        }}
                    >
                        Clear Filters
                    </Button>
                </Box>

            </Stack>
        </Paper>

        {/* 4. Bank Cards Grid */}
        <Typography variant="h6" fontWeight="600" mb={3} sx={{ color: '#fff' }}>
            Banking Relationships
        </Typography>

        <Grid container spacing={3}>
            {bankListData.length > 0 ? bankListData.map((bank, index) => (
                <Grid item xs={12} md={4} key={index}>
                    <Paper 
                        sx={{ 
                            borderRadius: 3, 
                            p: 0, 
                            overflow: 'hidden',
                            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                            // Keep the requested Blue Border, but maybe slightly brighter for dark mode
                            borderTop: "6px solid #1976d2", 
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            bgcolor: '#1e1e1e', // Dark Card
                            color: '#fff',
                            borderLeft: '1px solid #333',
                            borderRight: '1px solid #333',
                            borderBottom: '1px solid #333'
                        }}
                    >
                        {/* Card Header */}
                        <Box p={3} display="flex" alignItems="flex-start" justifyContent="space-between">
                            <Box display="flex" gap={2} alignItems="center">
                                <Avatar sx={{ bgcolor: 'rgba(25, 118, 210, 0.15)', color: '#42a5f5' }}>
                                    <BusinessIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="h6" fontWeight="bold">
                                        {bank.bankName}
                                    </Typography>
                                    <Chip 
                                        label={bank.relationship} 
                                        size="small" 
                                        sx={{ 
                                            height: 22, 
                                            fontSize: '0.7rem', 
                                            bgcolor: 'rgba(25, 118, 210, 0.15)', 
                                            color: '#90caf9',
                                            fontWeight: 600,
                                            mt: 0.5
                                        }} 
                                    />
                                </Box>
                            </Box>
                        </Box>

                        <Divider sx={{ borderStyle: 'dashed', borderColor: 'rgba(255,255,255,0.1)' }} />

                        {/* Card Stats */}
                        <Box p={3} flexGrow={1}>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Box>
                                        <Typography variant="caption" sx={{ color: '#b0b3b8', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <AccountBalanceWalletIcon sx={{ fontSize: 16 }} /> Total Balance
                                        </Typography>
                                        <Typography variant="h5" fontWeight="bold" mt={0.5} color="#fff">
                                            {formatCurrency(bank.totalBalance)}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sx={{ textAlign: 'right' }}>
                                    <Box>
                                        <Typography variant="caption" sx={{ color: '#b0b3b8', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                            <CreditCardIcon sx={{ fontSize: 16 }} /> Accounts
                                        </Typography>
                                        <Typography variant="h5" fontWeight="bold" mt={0.5} color="#fff">
                                            {bank.creditCardsCount}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Card Footer */}
                        <Box p={2} bgcolor="#252525" borderTop="1px solid rgba(255,255,255,0.05)">
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="caption" sx={{ color: '#777', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CalendarTodayIcon sx={{ fontSize: 13 }} /> Last Activity: {bank.lastActivity}
                                </Typography>
                                <Button size="small" sx={{ textTransform: 'none', fontSize: '0.75rem', color: '#90caf9' }}>
                                    View Details
                                </Button>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>
            )) : (
                <Grid item xs={12}>
                    <Paper sx={{ p: 4, textAlign: 'center', bgcolor: '#1e1e1e', borderRadius: 3 }}>
                        <Typography color="#b0b3b8">No banking relationships found.</Typography>
                    </Paper>
                </Grid>
            )}
        </Grid>

        {/* Autopay Panel */}
        <Box mb={3}>
            <Typography variant="h6" fontWeight="600" mb={1} sx={{ color: '#fff' }}>
                Autopay & Statement Summary
            </Typography>
            <Paper sx={{ p: 2, bgcolor: '#151515', borderRadius: 2 }}>
                {autopayRows.length === 0 ? (
                    <Typography sx={{ color: '#b0b3b8' }}>No autopay entries found for this user.</Typography>
                ) : (
                    autopayRows.map((r) => (
                        <Box key={r.id} display="flex" justifyContent="space-between" alignItems="center" sx={{ py: 1, borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                            <Box>
                                <Typography sx={{ color: '#fff', fontWeight: 600 }}>{r.card_name || r.card_identifier}</Typography>
                                <Typography variant="caption" sx={{ color: '#b0b3b8' }}>{r.card_number ? (`****${(r.card_number || '').toString().slice(-4)}`) : '—'} • {r.bank_account_name || ''}</Typography>
                            </Box>
                            <Box textAlign="right">
                                <Typography sx={{ color: '#fff' }}>{r.payment_autopay_date ? new Date(r.payment_autopay_date).toLocaleDateString() : '—'}</Typography>
                                <Typography variant="caption" sx={{ color: '#b0b3b8' }}>Due</Typography>
                            </Box>
                        </Box>
                    ))
                )}
            </Paper>
        </Box>

        <Box mt={4}>
            <Grid container spacing={3} alignItems="stretch">
                
                <Grid item xs={12} md={6} width={'45%'}>
                    <CreditUtilizationPanel bankListData={accountStats} />
                </Grid>

                <Grid item xs={12} md={6} width={'45%'}>
                    <BalanceOverviewPanel bankListData={accountStats} />
                </Grid>

            </Grid>
        </Box>
    </Box>
  );
}